/**
 * Reiter *Klärfälle* (`unallocated-list`, Spec 04c, „Reiter Klärfälle") —
 * Zahlungen mit Rest ≠ 0, aus `GET /billing/unallocated`. Eine Zeile öffnet
 * `PaymentSheet` im **dritten Einstieg** (`onSelect`): Kopfdaten stehen fest,
 * darunter die Zuordnungsliste der offenen Posten dieses Mieters — dieselbe
 * Mechanik wie beim ersten Einstieg, siehe `PaymentSheet.tsx`.
 *
 * Zeile als `<button>` in einem `<li>`, wie `ChargeRow`
 * (`features/billing/ChargesList.tsx`): kein `Link`, das Sheet ist kein
 * eigener Bildschirm.
 *
 * `*_display` kommt wörtlich vom Server (I9); diese Liste rechnet nichts —
 * auch die Summenzeile ist `totals` aus der Antwort, nicht `reduce()` über
 * die ausgelieferte Seite (die kann kleiner sein als die gefilterte Menge).
 */

import type { UseQueryResult } from '@tanstack/react-query'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { formatDate } from '@/lib/date'
import { pluralize } from '@/lib/pluralize'
import type { UnallocatedPage, UnallocatedPayment } from '@/features/billing/api'

interface UnallocatedListProps {
  readonly query: UseQueryResult<UnallocatedPage>
  readonly onSelect: (item: UnallocatedPayment) => void
}

export function UnallocatedList({ query, onSelect }: UnallocatedListProps) {
  return (
    <div data-testid="unallocated-list" className="flex flex-col">
      {query.isPending && <SkeletonRows testId="unallocated-loading" rows={5} />}

      {query.isError && (
        <ErrorState
          testId="unallocated-error"
          retryTestId="unallocated-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && query.data.items.length === 0 && (
        <EmptyState
          testId="unallocated-empty"
          title="Keine offenen Klärfälle."
          hint="Zahlungen ohne vollständige Zuordnung erscheinen hier."
        />
      )}

      {query.isSuccess && query.data.items.length > 0 && (
        <>
          {/* Spaltenköpfe nur am Schreibtisch (Spec 04c, „Sprache") — unter
           * 768px ersetzt die Kartenform (Spec 04c, „Schmale Ansicht") die
           * Tabelle vollständig, dieselbe Reihenfolge wie `UnallocatedRow`. */}
          <div className="text-label text-muted border-border hidden border-b py-1 pr-(--page-x) pl-3 md:flex md:gap-3">
            <span className="w-24">Datum</span>
            <span className="w-40">Mieter</span>
            <span className="flex-1">Verwendungszweck</span>
            <span className="w-32">Zahlweise</span>
            <span className="w-24 text-right">Betrag</span>
            <span className="w-24 text-right">Rest</span>
          </div>
          <ul>
            {query.data.items.map((item) => (
              <UnallocatedRow
                key={item.id}
                item={item}
                onClick={() => {
                  onSelect(item)
                }}
              />
            ))}
          </ul>

          <div
            data-testid="unallocated-totals"
            data-numeric
            className="border-border bg-surface sticky bottom-0 flex justify-end gap-3 border-t px-(--page-x) py-2"
          >
            {/* „1 Klärfälle" bei genau einem Fall — dem Normalfall gegen den
             * vollen Seed (Spec 04c „Der Leerzustand ist nicht selbstver-
             * ständlich prüfbar") — war das Kopiervorbild `{count} Posten`
             * (04a), das nur zufällig trug: „Posten" bleibt im Deutschen
             * unverändert, „Klärfälle" nicht (Befund K-D1). */}
            <span className="text-label text-muted">
              {query.data.totals.count} {pluralize(query.data.totals.count, 'Klärfall', 'Klärfälle')}
            </span>
            <span className="text-body text-text font-medium">{query.data.totals.unallocated_display}</span>
          </div>
        </>
      )}
    </div>
  )
}

function UnallocatedRow({ item, onClick }: { readonly item: UnallocatedPayment; readonly onClick: () => void }) {
  return (
    <li className="border-b-border border-b last:border-b-0">
      <button
        type="button"
        data-testid={`unallocated-row-${item.id}`}
        onClick={onClick}
        className="hover:bg-bg flex min-h-(--row-h) w-full flex-col gap-1 py-2 pr-(--page-x) pl-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out) md:min-h-(--row-h) md:flex-row md:items-center md:gap-3 md:py-1"
      >
        {/* Schmale Ansicht (Spec 04c, „Schmale Ansicht"): Mieter und Datum in
         * Zeile 1, Verwendungszweck in Zeile 2, Betrag und Rest rechts — am
         * Schreibtisch dieselben Felder als Spalten. */}
        <span className="flex items-center justify-between gap-2 md:contents">
          <span className="text-body text-text md:order-2 md:w-40 md:font-medium">{item.tenant.label}</span>
          <span className="text-label text-muted md:order-1 md:w-24">{formatDate(item.received_on)}</span>
        </span>
        <span className="text-label text-muted flex flex-col gap-0.5 truncate md:order-3 md:flex-1">
          <span>{item.reference ?? ''}</span>
          {item.reserved_cents > 0 && <span data-numeric>davon vorgemerkt {item.reserved_display}</span>}
        </span>
        <span className="text-label text-muted md:order-4 md:w-32">{item.method.label}</span>
        <span data-numeric className="text-body text-text md:order-5 md:w-24">
          {item.amount_display}
        </span>
        <span data-numeric className="text-body text-text md:order-6 md:w-24 font-medium">
          {item.unallocated_display}
        </span>
      </button>
    </li>
  )
}
